import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';
import API_BASE from '../../utils/api';

const token = () => localStorage.getItem('token');
const headers = () => ({ Authorization: `Bearer ${token()}` });

const METHODS = [
  {
    key: 'cctvFacialRecognitionEnabled',
    icon: '📹',
    title: 'CCTV Facial Recognition',
    desc: 'An external CCTV / facial-recognition system identifies the teacher and reports the punch to this app — no camera feed or face matching happens here.',
    method: 'cctv_facial',
    identifier: 'employeeId'
  },
  {
    key: 'biometricRfidEnabled',
    icon: '🪪',
    title: 'Biometric / RFID',
    desc: 'A biometric or RFID attendance device (or its middleware/agent) reports the punch to this app once the teacher’s card/fingerprint is registered against their profile.',
    method: 'biometric_rfid',
    identifier: 'rfidCardId'
  }
];

const AttendanceDeviceConfig = () => {
  const [form, setForm] = useState({ cctvFacialRecognitionEnabled: false, biometricRfidEnabled: false });
  const [apiKey, setApiKey] = useState('');
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: '', text: '' }), 5000); };

  const load = () => {
    axios.get(`${API_BASE}/settings/attendance-device-config`, { headers: headers() })
      .then(res => {
        setForm({
          cctvFacialRecognitionEnabled: !!res.data.cctvFacialRecognitionEnabled,
          biometricRfidEnabled: !!res.data.biometricRfidEnabled
        });
        setApiKey(res.data.attendanceDeviceApiKey || '');
        setConfigured(res.data.configured);
      })
      .catch(() => flash('error', 'Failed to load attendance device configuration'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const toggle = (key) => setForm(f => ({ ...f, [key]: !f[key] }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_BASE}/settings/attendance-device-config`, form, { headers: headers() });
      flash('success', 'Attendance device configuration saved');
    } catch (err) {
      flash('error', err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleRegenerate = async () => {
    if (configured && !window.confirm('Regenerating the API key will stop any device using the old key from reporting attendance until it is updated with the new key. Continue?')) {
      return;
    }
    setRegenerating(true);
    try {
      const res = await axios.post(`${API_BASE}/settings/attendance-device-config/regenerate-key`, {}, { headers: headers() });
      setApiKey(res.data.attendanceDeviceApiKey);
      setConfigured(true);
      setShowKey(true);
      flash('success', 'API key regenerated — copy it now, it won’t be shown in full again');
    } catch (err) {
      flash('error', err.response?.data?.error || 'Failed to regenerate key');
    } finally { setRegenerating(false); }
  };

  const copyKey = () => {
    navigator.clipboard?.writeText(apiKey);
    flash('success', 'API key copied to clipboard');
  };

  const webhookUrl = `${window.location.origin.replace(':3000', ':5001')}/api/attendance-devices/event`;

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-100">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center"><div className="text-gray-500">Loading...</div></div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-semibold text-gray-800">Teacher Attendance Devices</h1>
              <p className="text-xs text-gray-400 mt-0.5">Enable CCTV facial recognition and biometric/RFID attendance integrations</p>
            </div>
            <ProfileDropdown />
          </div>
        </nav>

        <div className="flex-1 overflow-auto p-6 space-y-6">
          {msg.text && (
            <div className={`px-4 py-3 rounded-lg text-sm font-medium ${msg.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
              {msg.text}
            </div>
          )}

          {/* Method toggles */}
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <h3 className="font-semibold text-gray-800">Attendance Methods</h3>
            {METHODS.map(m => (
              <div key={m.key} className="flex items-start justify-between gap-4 p-4 rounded-xl border border-gray-200">
                <div className="flex items-start gap-3">
                  <div className="text-2xl">{m.icon}</div>
                  <div>
                    <p className="font-medium text-gray-800">{m.title}</p>
                    <p className="text-xs text-gray-400 mt-0.5 max-w-xl">{m.desc}</p>
                    <p className="text-xs text-gray-400 mt-1">Matches teachers by their <code className="bg-gray-100 px-1 rounded">{m.identifier}</code>, sent as <code className="bg-gray-100 px-1 rounded">{m.method}</code>.</p>
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={form[m.key]}
                  onClick={() => toggle(m.key)}
                  className={`shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition ${form[m.key] ? 'bg-indigo-600' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${form[m.key] ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            ))}
            <div className="pt-2">
              <button onClick={handleSave} disabled={saving} className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium">
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>

          {/* API key + webhook */}
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="font-semibold text-gray-800">Device API Key</h3>
              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${configured ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {configured ? '✓ Generated' : 'Not generated'}
              </span>
            </div>
            <p className="text-xs text-gray-400">Give this key to the external CCTV/facial-recognition system or biometric/RFID device integration — it authenticates as <code className="bg-gray-100 px-1 rounded">x-api-key</code> when it POSTs attendance events.</p>

            <div className="flex items-center gap-2">
              <input
                readOnly
                type={showKey ? 'text' : 'password'}
                value={apiKey || 'No key generated yet'}
                className="flex-1 border rounded-lg px-3 py-2 font-mono text-sm bg-gray-50 text-gray-700"
              />
              {apiKey && (
                <>
                  <button type="button" onClick={() => setShowKey(s => !s)} className="text-xs text-gray-500 hover:text-gray-700 px-2">{showKey ? 'Hide' : 'Show'}</button>
                  <button type="button" onClick={copyKey} className="text-xs text-indigo-600 hover:text-indigo-800 px-2">Copy</button>
                </>
              )}
            </div>

            <button type="button" onClick={handleRegenerate} disabled={regenerating} className="border border-indigo-600 text-indigo-600 px-4 py-2 rounded-lg hover:bg-indigo-50 disabled:opacity-50 font-medium text-sm">
              {regenerating ? 'Generating...' : configured ? 'Regenerate API Key' : 'Generate API Key'}
            </button>

            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <p className="text-xs font-semibold text-blue-800 mb-1">Webhook Endpoint</p>
              <code className="text-xs text-blue-700 font-mono break-all">POST {webhookUrl}</code>
              <p className="text-xs text-blue-600 mt-2">Body: <code className="bg-white/60 px-1 rounded">{'{ method, deviceId, eventId, timestamp, employeeId | rfidCardId, confidence?, snapshotImage? }'}</code></p>
            </div>
          </div>

          <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
            Teachers must have their Employee ID (for CCTV facial recognition) or RFID Card ID (for biometric/RFID, set from Teachers → Edit) registered before device events can be matched to them.
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceDeviceConfig;
